import { useIntl } from 'react-intl';

import { DownloadIcon } from '@dynatrace/strato-icons';

import { Button } from '../../../buttons/button/Button.js';
import { downloadBlob } from '../../../core/utils/download.js';
import { Tooltip } from '../../../overlays/tooltip/Tooltip.js';
import type { HighlightLanguage } from '../HighlightExtended.js';
import { resolveDownloadFileName } from '../utils/download-utils.js';
import { codeSnippetActionsMenuMessages as messages } from './actions-menu-messages.js';

/**
 * Props for the `CodeSnippet.Download` slot.
 * @public
 */
export interface CodeSnippetDownloadProps {
  /**
   * Name of the downloaded file. If no extension is provided, it is derived
   * from the `language` prop. Both the filename and extension are sanitized to keep
   * only letters, digits, `-`, and `_`.
   * @defaultValue 'code-snippet.\<derived extension\>'
   */
  fileName?: string;
  /**
   * Callback fired when the download action is triggered.
   */
  onDownload?: () => void;
}

/**
 * Slot that enables a Download action inside `CodeSnippet.ActionsMenu`.
 * @public
 */
export const Download = (props: CodeSnippetDownloadProps): null => null;
(Download as typeof Download & { displayName: string }).displayName =
  'CodeSnippet.Download';

/**
 * Hands the snippet to the browser as a text file download and notifies the consumer.
 * @param snippetContent - The snippet's plain-text content.
 * @param downloadConfig - Configuration of the Download action.
 * @param language - The snippet's highlight language, used to derive the file extension.
 */
export function downloadSnippet(
  snippetContent: string,
  { fileName, onDownload }: CodeSnippetDownloadProps,
  language?: HighlightLanguage,
) {
  downloadBlob(
    new Blob([snippetContent], { type: 'text/plain;charset=utf-8' }),
    resolveDownloadFileName(fileName, language),
  );
  onDownload?.();
}

/**
 * Props for the `DownloadInternal` component.
 */
interface DownloadInternalProps {
  /**
   * Layout size of the snippet, matched by the button.
   */
  size: 'default' | 'condensed';
  /**
   * Callback fired when the button is activated.
   */
  onSelect: () => void;
}

/**
 * Renders `CodeSnippet.Download` as a standalone button.
 * @internal
 */
export const DownloadInternal = ({ size, onSelect }: DownloadInternalProps) => {
  const intl = useIntl();
  const downloadLabel = intl.formatMessage(messages.download);

  return (
    <Tooltip text={downloadLabel} placement="top" delay="none">
      <Button
        variant="default"
        size={size}
        aria-label={downloadLabel}
        onClick={onSelect}
        data-dt-children-name="Download"
      >
        <Button.Prefix>
          <DownloadIcon />
        </Button.Prefix>
      </Button>
    </Tooltip>
  );
};
